'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/lib/supabase/client'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { toast } from 'sonner'

const forgotPasswordSchema = z.object({
 email: z.string().email('Invalid email address'),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

export default function ForgotPasswordPage() {
 const [loading, setLoading] = useState(false)
 const router = useRouter()

 const {
 register,
 handleSubmit,
 formState: { errors },
 } = useForm<ForgotPasswordFormData>({
 resolver: zodResolver(forgotPasswordSchema),
 })

 const onSubmit = async (data: ForgotPasswordFormData) => {
 setLoading(true)

 try {
 const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
 redirectTo: `${window.location.origin}/auth/reset-password`,
 })

 if (error) {
 toast.error(error.message)
 return
 }

 toast.success('Password reset email sent! Check your inbox.')
 router.push('/auth/login')
 } catch (error) {
 toast.error('An unexpected error occurred. Please try again.')
 } finally {
 setLoading(false)
 }
 }

 return (
 <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
 <div className="max-w-md w-full space-y-8">
 <div>
 <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
 Forgot your password?
 </h2>
 <p className="mt-2 text-center text-sm text-gray-600">
 Enter your email address and we'll send you a link to reset your password.
 </p>
 </div>
 <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
 <div className="space-y-4">
 <div>
 <label htmlFor="email" className="block text-sm font-medium text-gray-700">
 Email Address
 </label>
 <input
 id="email"
 type="email"
 {...register('email')}
 className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
 placeholder="Email address"
 />
 {errors.email && (
 <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
 )}
 </div>
 </div>

 <div>
 <button
 type="submit"
 disabled={loading}
 className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {loading ? 'Sending...' : 'Send Reset Link'}
 </button>
 </div>

 <div className="text-center">
 <Link href="/auth/login" className="font-medium text-indigo-600 hover:text-indigo-500">
 Back to sign in
 </Link>
 </div>
 </form>
 </div>
 </div>
 )
}